import { useState, useCallback } from 'react';
import tripPhotoService from '../services/trips/trip-photo.service';

export interface AddTripPhotoData {
  tripId: string;
  imageUrl: string;
  caption?: string;
  latitude?: number;
  longitude?: number;
  altitude?: number;
  placeId?: string;
  placeName?: string;
}

export const useAddTripPhoto = (
  tripId: string,
  onClose: () => void,
  onSuccess?: () => void
) => {
  const [photoData, setPhotoData] = useState<AddTripPhotoData>({
    tripId,
    imageUrl: '',
    caption: '',
    latitude: undefined,
    longitude: undefined,
    altitude: undefined,
    placeId: undefined,
    placeName: undefined
  });

  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback(
    (field: keyof AddTripPhotoData, value: any) => {
      setPhotoData((prev) => {
        const updated = { ...prev, [field]: value };

        // Validate that we have required fields
        const isValid = !!(updated.imageUrl && updated.tripId);
        setValidated(isValid);

        return updated;
      });
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validated || isSubmitting) {
        return;
      }

      setIsSubmitting(true);

      try {
        // Convert data URL to File if needed
        let fileToUpload: File | null = null;

        if (photoData.imageUrl.startsWith('data:')) {
          // Convert base64 to blob
          const base64Response = await fetch(photoData.imageUrl);
          const blob = await base64Response.blob();
          fileToUpload = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
        }

        if (!fileToUpload) {
          throw new Error('Invalid image data');
        }

        // Upload the photo
        await tripPhotoService.uploadPhoto(
          photoData.tripId,
          fileToUpload,
          photoData.caption
        );

        // Call success callback
        if (onSuccess) {
          onSuccess();
        }

        // Close the modal
        onClose();
      } catch (error: any) {
        console.error('Error adding trip photo:', error);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [photoData, validated, isSubmitting, onClose, onSuccess]
  );

  return {
    photoData,
    validated,
    isSubmitting,
    handleInputChange,
    handleSubmit
  };
};
